(() => {
  angular
    .module('gm.common.content')
    .factory('GmyWordpressApi', GmyWordpressApi);

  function GmyWordpressApi(
    $http,
    $log,
    $window,
    GmyUtils,
    CacheFactory,
    Config
  ) {
    'ngInject';

    const API = {
      page: {
        API_HOST: 'https://content.goldmoney.com/wp-json/wp/v2/',
      },
      news: {
        API_HOST: 'https://content.goldmoney.com/news/wp-json/wp/v2/',
      },
      media: {
        API_HOST: 'https://content.goldmoney.com/media/wp-json/wp/v2/',
      },
      research: {
        API_HOST: 'https://content.goldmoney.com/insights/wp-json/wp/v2/',
      },
      wealth: {
        API_HOST: 'https://content.goldmoney.com/wealth/wp-json/wp/v2/',
      }
    };

    const CACHE = CacheFactory('wordpress', {
      storageMode: 'sessionStorage',
      maxAge: Config.wordpress.cache.maxAge,
      deleteOnExpire: 'aggressive',
      recycleFreq: Config.wordpress.cache.recycleFreq
    });
    const CACHE_TIMESTAMP = `${$window.moment().unix()}`;

    return {
      getCategories,
      getCategoryContent,
      getCategoriesById,
      getCategoryBySlug,
      getFeaturedPosts,
      getPageBySlug,
      getPostBySlug,
      getPosts,
      getRecommendedArticles
    };

    /**
     * Setup AJAX call
     *
     * @param  {String}  method                   GET/POST/UPDATE
     * @param  {String}  url                      API endpoint
     * @param  {Boolean} isCached                 flag
     * @param  {Boolean} isCacheTimestampRequired flag
     * @return {Object}
     */
    function callApi(method, origUrl, params, isCached = true, isCacheTimestampRequired = true) {
      let hasParams = false;

      let url = origUrl;
      if (params !== null) {
        url = `${url}?${GmyUtils.queryString(params)}`;
        hasParams = true;
      }

      url += ((isCacheTimestampRequired) ? ((hasParams) ? '&' : '?') + CACHE_TIMESTAMP : '');

      return $http({
        method,
        cache: (isCached) ? CACHE : false,
        url
      });
    }

    /**
     * Get all categories from database
     *
     * @param  {String}  type     API endpoint
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getCategories(type, isCached) {
      const url = `${API[type].API_HOST}categories`;

      return callApi('GET', url, null, isCached)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, null, isCached);
        });
    }

    /**
     * Get categories that are defined by list
     *
     * @param  {Array}   list     Categories ids list
     * @param  {String}  type     API endpoint
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getCategoriesById(list, type, isCached) {
      // convert array into the comma separated string
      const include = list.join(',');

      const params = {
        include,
      };
      const url = `${API[type].API_HOST}categories`;

      return callApi('GET', url, params, isCached)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, params, isCached);
        });
    }

    /**
     * Get category by slug
     *
     * @param  {Object}  config   Request config
     * @param  {String}  slug     Page slug
     * @param  {String}  type     API endpoint
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getCategoryBySlug(config, slug, type, isCached) {
      const url = `${API[type].API_HOST}posts`;
      const params = {
        per_page: config.perPage,
        offset: config.offset || 0
      };

      return getCategoryIdBySlug(slug, type)
        .then((data) => {
          if (data.data.length === 0) {
            return null;
          }

          params.categories = data.data[0].id;

          if (config.embed) {
            params._embed = null;
          }

          return callApi('GET', url, params, isCached);
        })
        .catch((error) => {
          $log.debug(error);

          return callApi('GET', url, params, isCached);
        });
    }

    /**
     * Retrieve category ID by it's slug
     *
     * @param  {String} slug
     * @param  {String} type
     * @return {Object}
     */
    function getCategoryIdBySlug(slug, type) {
      const url = `${API[type].API_HOST}categories`;

      return callApi('GET', url, { slug }, false)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, { slug }, false);
        });
    }

    /**
     * Get articles that belongs to the category
     *
     * @param  {Number}  id       Category id
     * @param  {String}  type     API endpoint
     * @param  {Object}  config   Request parameters
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getCategoryContent(id, type, config, isCached) {
      const params = {
        categories: id,
        per_page: config.perPage,
        offset: config.offset || 0
      };

      const url = `${API[type].API_HOST}posts`;

      if (config.embed) {
        params._embed = null;
      }

      return callApi('GET', url, params, isCached)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, params, isCached);
        });
    }

    /**
     * Get featured post
     *
     * @param  {Number}  id       "Featured" tag ID (= 6)
     * @param  {String}  type     API endpoint
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getFeaturedPosts(id, type, isCached) {
      const params = {
        tags: id,
        _embed: null
      };
      const url = `${API[type].API_HOST}posts`;

      return callApi('GET', url, params, isCached)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, params, isCached);
        });
    }

    /**
     * Get page by slug
     *
     * @param  {String}  slug     Page slug
     * @param  {String}  type     API endpoint
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getPageBySlug(slug, type, isCached) {
      const params = {
        slug
      };
      const url = `${API[type].API_HOST}pages`;

      return callApi('GET', url, params, isCached)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, params, isCached);
        });
    }

    /**
     * Get post by slug
     *
     * @param  {String}  slug     Post slug
     * @param  {String}  type     API endpoint
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getPostBySlug(slug, type, isCached) {
      const params = {
        slug,
        _embed: null
      };
      const url = `${API[type].API_HOST}posts`;

      return callApi('GET', url, params, isCached)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, params, isCached);
        });
    }

    /**
     * Get posts from database
     *
     * @param  {Object}  config   Request parameters
     * @param  {String}  type     API endpoint
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getPosts(config, type, isCached) {
      const params = {
        per_page: config.perPage,
        offset: config.offset || 0
      };

      if (config.embed) {
        params._embed = null;
      }

      if (config.categories) {
        params.categories = config.categories;
      }

      const url = `${API[type].API_HOST}posts`;

      return callApi('GET', url, params, isCached)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, params, isCached);
        });
    }

    /**
     * Get articles that has "recommended" tag
     *
     * @param  {Number}  id       Recommended tag id
     * @param  {String}  type     API endpoint
     * @param  {Boolean} isCached Cache response flag
     * @return {Object}           promise
     */
    function getRecommendedArticles(id, type, isCached) {
      const params = {
        tags: id,
        _embed: null
      };
      const url = `${API[type].API_HOST}posts`;

      return callApi('GET', url, params, isCached)
        .catch((error) => {
          $log.debug(error);
          return callApi('GET', url, params, isCached);
        });
    }
  }
})();
